import React, { useCallback } from 'react'

import { useState, useEffect } from 'react'
import {
  Button,
  Form,
  Grid,
  GridContainer,
  GridItem,
  Modal,
  Spinner,
} from '@enterprise-ui/canvas-ui-react'
import {
  DocPrefixMapsResponse,
  GET_DOCPREFIXMAPS,
  GET_RESOLUTIONCODEMAPS,
  ResolutionCodeMaps,
} from '../../Cases/Common/Queries'
import {
  CreateResolutionCodeMapVar,
  CREATE_RESOLUTIONCODE_MAP,
} from '../common/AdminQueries'
import { useMutation, useQuery } from '@apollo/client'
import { useFormik } from 'formik'
import {
  getUniqueCaseSubTypes,
  getUniqueCaseTypes,
  getUniqueDocumentSubTypes,
  getUniqueDocumentTypes,
} from '../common/DoctypeHelper'
import { Autocomplete } from '@enterprise-ui/canvas-ui-react-autocomplete'
import DragNDropList from '../common/DragNDropList'
import { ResolutionCode } from './ResolutionCodesAdmin'
import { InitialResolutioncodes } from './EditResolutionCodeMap'
import * as Yup from 'yup'

export interface ResolutionCodeMapForm {
  caseType?: string
  caseSubType?: string
  documentTypeId?: number
  documentTypeText?: string
  documentSubTypeId?: number
  documentSubTypeText?: string
  active?: boolean
  resolutionCodes?: ResolutionCode[]
  initialResolutionCodes?: InitialResolutioncodes
}

const schema = Yup.object().shape<ResolutionCodeMapForm>({
  caseType: Yup.string().required('Select a Case Type'),
  caseSubType: Yup.string().required('Select a Case Sub Type'),
})

const initialValues: ResolutionCodeMapForm = {
  caseSubType: undefined,
  caseType: undefined,
  documentTypeId: undefined,
  documentTypeText: undefined,
  documentSubTypeId: undefined,
  documentSubTypeText: undefined,
  active: true,
  resolutionCodes: [],
  initialResolutionCodes: {
    all_resolution: {
      title: 'All Resolution Codes',
      items: [],
    },
    selected_resolutions: {
      title: 'Selected Resolution Codes',
      items: [],
    },
  },
}

export const AddResolutionCodeMap = (props: any) => {
  const [resolutionCodes, setResolutionCodes] = useState<ResolutionCode[]>()
  const formik = useFormik<ResolutionCodeMapForm>({
    initialValues: { ...initialValues },
    onSubmit: async (values) => {
      await updateResolutionCode({
        variables: {
          input: {
            caseType: values.caseType!!,
            caseSubType: values.caseSubType!!,
            active: values.active!!,
            documentTypeId: values.documentTypeId,
            documentTypeText: values.documentTypeText,
            documentSubTypeId: values.documentSubTypeId,
            documentSubTypeText: values.documentSubTypeText,
            resolutionCodeList: values.resolutionCodes?.map((reslnCode) => ({
              resolutionCodeId: Number(reslnCode.resolutionCodeId.toString()),
              resolutionCodeText: reslnCode.resolutionCodeText,
            })),
          },
        },
        refetchQueries: [{ query: GET_RESOLUTIONCODEMAPS }],
        awaitRefetchQueries: true,
      }).then(() => props.handleClose())
    },
    validateOnBlur: true,
    validationSchema: schema,
  })

  useEffect(() => {
    formik.setFieldValue('resolutionCodes', resolutionCodes)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolutionCodes])

  const { data: mappingValues } = useQuery<DocPrefixMapsResponse>(
    GET_DOCPREFIXMAPS,
    {
      onCompleted: (data) => {
        console.log(data.getDocumentTypeMaps)
      },
    },
  )

  const [updateResolutionCode, { loading }] = useMutation<
    ResolutionCodeMaps,
    CreateResolutionCodeMapVar
  >(CREATE_RESOLUTIONCODE_MAP, {
    onCompleted: (data) => {
      console.log(data)
    },
  })

  useEffect(() => {
    const resolutionCodes = (props.resolutionCodes || [])
      .filter((resolutionCode: any) => resolutionCode.active)

      .map(
        (resolutionCode: any) =>
          ({
            resolutionCodeId: String(resolutionCode.resolutionCodeId),
            resolutionCodeText: resolutionCode.resolutionCodeText,
          }) as ResolutionCode,
      )

    formik.setFieldValue('initialResolutionCodes', {
      all_resolutions: {
        title: 'All Resolution Codes',
        items: [...resolutionCodes],
      },
      selected_resolutions: {
        title: 'Selected Resolution Codes',
        items: [],
      },
    })

    formik.setFieldValue('caseType', undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.resolutionCodes])

  const handleListChange = useCallback(
    (updatedColumns: InitialResolutioncodes) => {
      setResolutionCodes(updatedColumns.selected_resolutions?.items ?? [])
    },
    [],
  )

  return (
    <Modal
      headingText="Create Resolution Code"
      onRefuse={() => {
        props.handleClose()
      }}
      isVisible
    >
      <Form onSubmit={formik.handleSubmit}>
        <Grid.Container justify={'center'} className="hc-pa-normal">
          <Grid.Item xs={11}>
            <Grid.Container>
              <Grid.Item xs={6}>
                <Form.Field
                  id="caseType"
                  label={'Case Type'}
                  type="select"
                  required
                  options={getUniqueCaseTypes(
                    mappingValues?.getDocumentTypeMaps,
                  )}
                  value={formik.values.caseType}
                  onUpdate={(_: any, value: any) => {
                    formik.setFieldValue('caseType', value)
                    formik.setFieldValue('caseSubType', '')
                    formik.setFieldValue('documentTypeId', undefined)
                    formik.setFieldValue('documentSubTypeId', undefined)
                  }}
                  error={
                    formik.touched.caseType &&
                    formik.errors.caseType !== undefined
                  }
                  errorText={formik.errors.caseType}
                />
              </Grid.Item>

              <Grid.Item xs={6}>
                <Form.Field
                  id="caseSubType"
                  label={'Case Sub Type'}
                  type="select"
                  required
                  options={getUniqueCaseSubTypes(
                    mappingValues?.getDocumentTypeMaps,
                    formik.values.caseType,
                  )}
                  value={formik.values.caseSubType}
                  onUpdate={(_: any, value: any) => {
                    formik.setFieldValue('caseSubType', value)
                    formik.setFieldValue('documentTypeId', undefined)
                    formik.setFieldValue('documentSubTypeId', undefined)
                  }}
                  error={
                    formik.touched.caseType &&
                    formik.errors.caseSubType !== undefined
                  }
                  errorText={formik.errors.caseSubType}
                />
              </Grid.Item>

              <Grid.Item xs={6}>
                <Autocomplete
                  id="documentTypeId"
                  label={'Document Type'}
                  onUpdate={(id, value) => {
                    formik.setFieldValue('documentTypeId', value?.value)
                    formik.setFieldValue('documentTypeText', value?.label)
                    formik.setFieldValue('documentSubTypeId', undefined)
                  }}
                  value={
                    formik.values.documentTypeId && {
                      id: formik.values.documentTypeId,
                      value: formik.values.documentTypeId,
                      label: formik.values.documentTypeText,
                    }
                  }
                  options={getUniqueDocumentTypes(
                    mappingValues?.getDocumentTypeMaps,
                    formik.values.caseType,
                    formik.values.caseSubType,
                  )}
                />
              </Grid.Item>

              <Grid.Item xs={6}>
                <Autocomplete
                  id="documentSubTypeId"
                  label={'Document Sub Type'}
                  onUpdate={(id, value) => {
                    formik.setFieldValue('documentSubTypeId', value?.value)
                    formik.setFieldValue('documentSubTypeText', value?.label)
                    console.log(formik.values)
                  }}
                  value={
                    formik.values.documentSubTypeId && {
                      id: formik.values.documentSubTypeId,
                      value: formik.values.documentSubTypeId,
                      label: formik.values.documentSubTypeText,
                    }
                  }
                  options={getUniqueDocumentSubTypes(
                    mappingValues?.getDocumentTypeMaps,
                    formik.values.caseType,
                    formik.values.caseSubType,
                    formik.values.documentTypeId,
                  )}
                />
              </Grid.Item>
              <Grid.Item xs={12}>
                <DragNDropList
                  initialColumns={formik.values.initialResolutionCodes}
                  onChange={handleListChange}
                />
              </Grid.Item>
            </Grid.Container>
          </Grid.Item>
          <GridItem xs={11}>
            <GridContainer align="center">
              <GridItem xs={3}>
                <Form.Field
                  id="active"
                  type="toggle"
                  label={'Active'}
                  value={formik.values.active}
                  onChange={() => {
                    formik.setFieldValue('active', !formik.values.active)
                  }}
                ></Form.Field>
              </GridItem>
              <GridItem justify="flex-end" xs={9}>
                <GridContainer direction="row-reverse" spacing="dense">
                  <GridItem>
                    <Button type="submit" disabled={loading}>
                      Save
                    </Button>
                  </GridItem>
                  <GridItem>
                    <Button
                      onClick={() => {
                        props.handleClose()
                      }}
                    >
                      Cancel
                    </Button>
                  </GridItem>

                  {loading && (
                    <GridItem>
                      <Spinner></Spinner>
                    </GridItem>
                  )}
                </GridContainer>
              </GridItem>
            </GridContainer>
          </GridItem>
        </Grid.Container>
      </Form>
    </Modal>
  )
}
